import { Button } from '@neo/primitives/button'
import { Input } from '@neo/primitives/input'
import { Label } from '@neo/primitives/label'
import { Textarea } from '@neo/primitives/textarea'
import { Typography } from '@neo/primitives/typography'
import { Plus, Trash2 } from 'lucide-react'
import type { ExtraVariableRow } from './collection-configuration-types'

const BLANK_EXTRA: ExtraVariableRow = {
  variable_name: '',
  variable_instructions: '',
  variable_format_instructions: '',
  variable_type: 'string',
  variable_enum_values: [],
  configure_data_format: false,
}

interface ExtraVariablesField {
  state: { value: ExtraVariableRow[] | null | undefined }
  pushValue: (row: ExtraVariableRow) => void
  removeValue: (index: number) => void
  replaceValue: (index: number, row: ExtraVariableRow) => void
}

/** The Variables tab's rows below the asked ones: what the agent notes from the call and never asks for. */
export const ExtraVariables = ({
  field,
  disabled,
  describingDots,
}: {
  field: ExtraVariablesField
  disabled: boolean
  describingDots: number
}) => {
  const rows = field.state.value ?? []
  // typing marks the field as the author's, emptying it gives it back to the fill
  const edit = (index: number, key: 'variable_name' | 'variable_instructions', text: string) => {
    const mark = key === 'variable_name' ? 'name' : 'description'
    field.replaceValue(index, {
      ...rows[index],
      [key]: text,
      overrides: { ...rows[index].overrides, [mark]: text.trim() ? true : undefined },
    })
  }
  return (
    <div className="flex flex-col gap-4 mb-4" data-testid="extra-variables">
      <div className="flex flex-col gap-1">
        <Typography variant="H4/500" className="text-foreground">
          Extra variables
        </Typography>
        <Typography variant="XS/400" className="text-secondary">
          Noted from what the caller says. The agent never asks for them.
        </Typography>
      </div>
      {rows.map((row, index) => (
        <div
          // biome-ignore lint/suspicious/noArrayIndexKey: a row is its place in the list, as phlo-core saves it
          key={index}
          className="flex flex-col gap-4 bg-bg3 p-4 rounded-md relative"
          data-testid="extra-variable-row"
        >
          <button
            type="button"
            className="absolute right-2 top-3 text-destructive hover:text-destructive/80"
            aria-label="Remove extra variable"
            disabled={disabled}
            onClick={() => field.removeValue(index)}
          >
            <Trash2 size={18} strokeWidth={1.5} />
          </button>
          <div className="space-y-2">
            <Label className="text-p-500">Variable name</Label>
            <Input
              value={row.variable_name}
              disabled={disabled}
              placeholder="e.g. pet_name"
              data-testid="input-extra-variable-name"
              onChange={(e) =>
                edit(
                  index,
                  'variable_name',
                  e.target.value.replace(/[^a-zA-Z0-9]/g, '_').toLowerCase(),
                )
              }
            />
          </div>
          <div className="space-y-2">
            <Label className="text-p-500">Description (what the agent notes, never asks)</Label>
            <Textarea
              value={row.variable_instructions}
              disabled={disabled || describingDots > 0}
              placeholder={
                describingDots
                  ? `Generating description${'.'.repeat(describingDots)}`
                  : 'Written when you save'
              }
              className={`min-h-[60px] bg-background${describingDots ? ' placeholder:italic' : ''}`}
              data-testid="textarea-extra-variable-instructions"
              onChange={(e) => edit(index, 'variable_instructions', e.target.value)}
            />
          </div>
        </div>
      ))}
      <Button
        variant="ghost"
        size="sm"
        type="button"
        className="w-fit gap-2"
        disabled={disabled}
        data-testid="button-add-extra-variable"
        onClick={() => field.pushValue({ ...BLANK_EXTRA })}
      >
        <Plus className="h-3.5 w-3.5" />
        Add extra variable
      </Button>
    </div>
  )
}
